import { Injectable } from '@angular/core';
import { RestApiClientAdapterService } from '../../sysbiz/rest-api-client-adapter.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService, IDtoGuid, PagedResultDto, SortDirectionDto } from '@systems/base';
import { TranslateService } from '@ngx-translate/core';
import { CUSTOMERAPICLIENT_CONFIG } from '../../sysbiz/config-loader.service';
import { map, Observable } from 'rxjs';
import { CompanyGroupDto, UserTitle } from '../../sysbiz/sysbiz-auth.service';
import { LanguageDto } from './models';
import { GroupDto } from './group.service';
import { ApplicationDto } from './application.service';

@Injectable({
  providedIn: 'root'
})
export class NewUserService extends RestApiClientAdapterService<NewUserDto, NewUserDto, WriteNewUserDto> {

  constructor(
    protected override http: HttpClient,
    authService: AuthService,
    protected translationService: TranslateService
  ) {
    super(CUSTOMERAPICLIENT_CONFIG, http, authService, 'users');
  }

  public listByFilters(filter: UserFilterDto): Observable<PagedResultDto<NewUserDto>> {

    let params = new HttpParams();

    if (filter.sorting) {
      params = params.append('sorting', filter.sorting as any);
    }
    if (filter.sortDirection) {
      params = params.append('sortDirection', filter.sortDirection as any);
    }
    if (filter.searchText) {
      params = params.append('searchText', filter.searchText as any);
    }
    if (filter.userGuid) {
      params = params.append('userGuid', filter.userGuid as any);
    }
    if (filter.emailAddress) {
      params = params.append('emailAddress', filter.emailAddress as any);
    }
    if (filter.pageNumber) {
      params = params.append('pageNumber', filter.pageNumber as any);
    }
    if (filter.pageSize) {
      params = params.append('pageSize', filter.pageSize as any);
    }
    if (filter.partnerGuid) {
      params = params.append('partnerGuid', filter.partnerGuid as any);
    }
    if (filter.customerGuid) {
      params = params.append('customerGuid', filter.customerGuid as any);
    }
    if (filter.companyGuid) {
      params = params.append('companyGuid', filter.companyGuid as any);
    }

    return this.http.get<PagedResultDto<NewUserDto>>(this.getUrl('users'), {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      params: params,
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public getUserGroups(guid: string): Observable<GroupDto[]> {
    return this.http.get<GroupDto[]>(this.getUrl(`users/${guid}/groups`), { headers: { authorization: this.authService.authenticationToken }, observe: 'response' })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public setUserGroups(guid: string, groups: number[]): Observable<GroupDto[]> {
    return this.http.put<GroupDto[]>(this.getUrl(`users/${guid}/groups`), groups, { headers: { authorization: this.authService.authenticationToken }, observe: 'response' })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public sendInvitation(
    guid: string
  ): Observable<NewUserDto> {

    return this.http.put<NewUserDto>(this.getUrl('users/' + guid + '/invitation'), null, {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }


  public addPartner(userGuid: string, partnerGuid: string) {
    return this.http.post<NewUserDto>(this.getUrl('users/' + userGuid + '/partner/' + partnerGuid), null, {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public removePartner(userGuid: string, partnerGuid: string) {
    return this.http.delete<NewUserDto>(this.getUrl('users/' + userGuid + '/partner/' + partnerGuid), {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public addCustomer(userGuid: string, customerGuid: string) {
    return this.http.post<NewUserDto>(this.getUrl('users/' + userGuid + '/customer/' + customerGuid), null, {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public removeCustomer(userGuid: string, customerGuid: string) {
    return this.http.delete<NewUserDto>(this.getUrl('users/' + userGuid + '/customer/' + customerGuid), {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }
  public removeCompany(userGuid: string, companyGuid: string) {
    return this.http.delete<NewUserDto>(this.getUrl('users/' + userGuid + '/company/' + companyGuid), {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public addCompany(userGuid: string, companyGuid: string, companyUserGroups?: number[]) {
    if (!companyUserGroups) {
      companyUserGroups = [];
    }

    return this.http.post<NewUserDto>(this.getUrl(`users/${userGuid}/company/${companyGuid}`), companyUserGroups, {
      headers: {
        authorization: this.authService.authenticationToken
      },
      observe: 'response',
      responseType: 'json',
    })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public updateSelectedCompany(selectedCompanyUid: string): Observable<boolean> {
    const userToken = this.Token;

    if (selectedCompanyUid)
      selectedCompanyUid = "\"" + selectedCompanyUid + "\"";

    return this.http.put<boolean>(this.getUrl('users/selectedCompany'), selectedCompanyUid, { headers: { 'authorization': userToken, 'Content-Type': 'application/json' }, observe: 'response' })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public addApplicationToUser(userGuid: string, apps: ApplicationDto[]): Observable<ApplicationDto[]> {
    return this.http.post<ApplicationDto[]>(
      this.getUrl(`users/${userGuid}/applications`),
      apps,
      {
        headers: { authorization: this.Token },
        observe: 'response',
        responseType: 'json'
      }
    )
    .pipe(
      map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      })
    );
  }

  public removeApplicationFromUser(userGuid: string, app: ApplicationDto): Observable<boolean> {
    return this.http.delete<boolean>(
      this.getUrl(`users/${userGuid}/application`),
      {
        headers: { authorization: this.Token },
        observe: 'response',
        responseType: 'json',
        body: app
      }
    )
    .pipe(
      map((resp: any) => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      })
    );
  }


  public getCurrentUser(): Observable<NewUserDto> {
    const userToken = this.Token;

    return this.http.get<NewUserDto>(this.getUrl('users/current'), { headers: { 'authorization': userToken }, observe: 'response' })
      .pipe(map(resp => {
        this.updateTokenFromResponse(resp);
        return resp.body!;
      }));
  }

  public listUserLicenses(guid: string) {
    return this.http.get<string[]>(this.getUrl(`users/${guid}/licenses`), { headers: { authorization: this.authService.authenticationToken }, observe: 'response' })
      .pipe(map(res => {
        this.updateTokenFromResponse(res);
        return res.body;
      }));
  }

  public getFeatureBaseToken() {
    return this.http.get(this.getUrl(`users/featurebaseToken`),
      {
        headers: { authorization: this.authService.authenticationToken },
        responseType: 'text',
        observe: 'response'
      }
    )
      .pipe(map(res => {
        this.updateTokenFromResponse(res);
        return res.body;
      }));
  }
}

export interface UserFilterDto {
  sorting?: NewUserSortingDto;
  sortDirection?: SortDirectionDto;
  searchText?: string;
  userGuid?: string;
  emailAddress?: string;
  pageNumber?: number;
  pageSize?: number;
  partnerGuid?: string;
  customerGuid?: string;
  companyGuid?: string;
}

export interface WriteNewUserDto {
  name: string;
  surname: string;
  active: boolean;
  emailAddress: string;
  taxNumber?: string;
  phoneNumber?: string;
  language: LanguageDto;
  title: UserTitle;
  groupIds: number[];
  customerGuid?: string;
  partnerGuid?: string;
  companyGuid?: string;
  isServiceAccount?: boolean;
}

export interface CreateNewUserDto extends WriteNewUserDto {
  password?: string;

  companyGroupIds: number[];
}

export enum NewUserSortingDto {
  IsActive = "IsActive",
  UserName = "UserName",
  FirstName = "FirstName",
  LastName = "LastName",
  TaxNumber = "TaxNumber",
  PhoneNumber = "PhoneNumber"
}

export interface NewUserDto extends WriteNewUserDto, IDtoGuid {
  companies: number[]
}


